"use client";

import {
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import {
  Icon,
  IconButton,
  Modal,
  ProgressBar,
  Select,
  type SelectListOption,
  Tooltip,
  useUuid,
  type ModalProps,
} from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import {
  calculateBoundsFromGeoJSON,
  getFoundationFeatureOpacity,
  type HotspotRecord,
  MapboxMapView,
  type MapboxMapViewProps,
  type GetTooltipContentHandler,
  type MapboxMapViewHandle,
  getMapboxDirections,
  addRouteLayer,
  removeRouteLayer,
} from "../../MapboxMapView";
import { useAmenityCategoryFilter, useClusteredHotspots } from "../../../hooks";
import { getAmenityHotspot } from "../../NeighbourhoodAnalysis/utils/getAmenityHotspot";
import { getMainHotspot } from "../../NeighbourhoodAnalysis/utils/getMainHotspot";
import type { GetGeoJSONHandler, GeoJSONType } from "../Analysis";
import { MessageBanner } from "./MessageBanner";
import { createCircleGeoJSON } from "../../NeighbourhoodAnalysis/utils/createCircleGeoJSON";
import { MapControlCard, type MapControlCardRef } from "../../MapControlCard";
import type { Amenity } from "../../../types";

import {
  cardStyle,
  closeButtonStyle,
  containerStyle,
  messageBannerStyle,
  progressBarStyle,
} from "./MapModal.css";
import { elevations } from "@huub-web/ui/theme/css/elevations.css";

/**
 * Selected route state containing amenity and route details
 */
type SelectedRouteState = {
  amenity: Amenity & { center: [number, number] };
  distance: number;
  duration: number;
} | null;

export type MapModalType = "general" | GeoJSONType;

export type MapModalProps = ModalProps & {
  mapBoxToken: string;
  center: [number, number];
  address: string;
  city: string;
  type?: MapModalType;
  loading?: boolean;
  getGeoJSON: GetGeoJSONHandler;
  /**
   * Amenities to display on the map when type is "general"
   */
  amenities?: Amenity[];
};

export const MapModal: FC<MapModalProps> = ({
  escape: escapable = true,
  closeable = false,
  mapBoxToken,
  center,
  address,
  type: typeProp = "general",
  loading: loadingProp = false,
  size = "auto",
  variant = "tint",
  city,
  onClose,
  getGeoJSON,
  amenities = [],
  ...props
}) => {
  const { t } = useTranslation();
  const id = useUuid();
  const mapRef = useRef<MapboxMapViewHandle>(null);
  const mapControlCardRef = useRef<MapControlCardRef>(null);
  const [type, setType] = useState<MapModalType>(typeProp);
  const [geoJSON, setGeoJSON] = useState<GeoJSON.FeatureCollection | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMapReady, setIsMapReady] = useState<boolean>(false);
  const [hasGeoJSONError, setHasGeoJSONError] = useState<boolean>(false);
  const [currentZoom, setCurrentZoom] = useState<number>(14);
  const [selectedRoute, setSelectedRoute] = useState<SelectedRouteState>(null);

  const mapTypeOptions: SelectListOption[] = [
    {
      label: t("analysis.mapModal.mapType.neighbourhood"),
      value: "neighbourhood",
    },
    { label: t("analysis.mapModal.mapType.district"), value: "district" },
    { label: t("analysis.mapModal.mapType.general"), value: "general" },
    { label: t("analysis.mapModal.mapType.foundation"), value: "foundation" },
  ];

  const {
    categories,
    selectedCategories,
    handleCategoryToggle,
    getCategoryCount,
  } = useAmenityCategoryFilter({ amenities });

  /**
   * Handle back button click to clear route and return to filters
   */
  const handleBack = () => {
    const map = mapRef.current?.getMap();
    if (map) {
      removeRouteLayer(map);
    }
    setSelectedRoute(null);
  };

  /**
   * Switch slides when route changes
   */
  useEffect(() => {
    if (selectedRoute) {
      mapControlCardRef.current?.showRoute();
    } else {
      mapControlCardRef.current?.showFilters();
    }
  }, [selectedRoute]);

  /**
   * Creates a 1km radius circle GeoJSON for general map type
   */
  const circleGeoJSON = useMemo<GeoJSON.FeatureCollection>(
    () =>
      createCircleGeoJSON({
        center,
        radiusInMeters: 1000,
      }),
    [center],
  );

  /**
   * Sync internal type state with prop
   */
  useEffect(() => {
    setType(typeProp);
  }, [typeProp]);

  /**
   * Fetches GeoJSON data when type changes, or uses circleGeoJSON for general type
   */
  useEffect(() => {
    if (loadingProp) {
      return;
    }

    if (type === "general") {
      setGeoJSON(circleGeoJSON);
      setHasGeoJSONError(false);
      return;
    }

    const fetchGeoJSON = async () => {
      setIsLoading(true);
      setHasGeoJSONError(false);

      try {
        const data = await getGeoJSON(type);

        if (!data) {
          setHasGeoJSONError(true);
          console.warn("No GeoJSON data found");
        } else if (data.features.length === 0) {
          setHasGeoJSONError(true);
          console.warn("No GeoJSON features found");
        }

        setGeoJSON(data);
      } catch (error) {
        console.error("Error fetching GeoJSON:", error);
        setGeoJSON(undefined);
        setHasGeoJSONError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGeoJSON();
  }, [type, getGeoJSON, loadingProp, circleGeoJSON]);

  /**
   * Fit map to GeoJSON bounds when data is loaded and map is ready
   */
  useEffect(() => {
    if (!mapRef.current || !isMapReady || !geoJSON) {
      return;
    }

    const bounds = calculateBoundsFromGeoJSON(geoJSON);
    if (bounds) {
      mapRef.current.fitBounds(bounds, {
        padding: 60,
        duration: 500,
        maxZoom: 16,
      });
    }
  }, [geoJSON, isMapReady]);

  const handleReady = useCallback(() => {
    setIsMapReady(true);
  }, []);

  /**
   * Handle viewport changes to track zoom level
   */
  const handleViewportChange = () => {
    const currentZoomLevel = mapRef.current?.getZoom();
    if (currentZoomLevel !== null && currentZoomLevel !== undefined) {
      setCurrentZoom(currentZoomLevel);
    }
  };

  /**
   * Handle cluster click to zoom in
   */
  const handleClusterClick = (
    e: MouseEvent<HTMLElement>,
    lat: number,
    lng: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const currentZoomLevel = mapRef.current?.getZoom() ?? 15;
    mapRef.current?.flyTo({
      center: [lng, lat],
      zoom: Math.max(currentZoomLevel + 1, 17),
      duration: 1500,
    });
  };

  /**
   * Handle amenity click to show navigation route
   */
  const handleAmenityClick = async (
    amenity: Amenity & { center: [number, number] },
  ) => {
    const map = mapRef.current?.getMap();
    if (!map || !mapBoxToken) return;

    const amenityId = `${amenity.name}-${amenity.center[0]}-${amenity.center[1]}`;
    const selectedAmenityId = selectedRoute
      ? `${selectedRoute.amenity.name}-${selectedRoute.amenity.center[0]}-${selectedRoute.amenity.center[1]}`
      : null;

    // If clicking the same amenity, clear the route
    if (selectedAmenityId === amenityId) {
      removeRouteLayer(map);
      setSelectedRoute(null);
      return;
    }

    const result = await getMapboxDirections({
      accessToken: mapBoxToken,
      origin: center,
      destination: amenity.center,
    });

    if (result) {
      addRouteLayer({
        map,
        route: result.route,
        origin: center,
        destination: amenity.center,
      });
      setSelectedRoute({
        amenity,
        distance: result.distance,
        duration: result.duration,
      });

      // Zoom to route bounds
      const coordinates = result.route.geometry.coordinates;
      const lngs = coordinates.map((c) => c[0]);
      const lats = coordinates.map((c) => c[1]);
      const bounds: [[number, number], [number, number]] = [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ];
      mapRef.current?.fitBounds(bounds, {
        padding: 150,
        duration: 1000,
      });
    }
  };

  const selectedAmenityId = selectedRoute
    ? `${selectedRoute.amenity.name}-${selectedRoute.amenity.center[0]}-${selectedRoute.amenity.center[1]}`
    : null;

  /**
   * Creates hotspots for the map including the main house marker
   * and amenity POI markers when type is "general"
   */
  const hotspots: HotspotRecord[] = (() => {
    // When route is active, only show main marker and active amenity
    if (selectedRoute && type === "general") {
      return [
        getAmenityHotspot({
          sessionID: id,
          address,
          amenity: selectedRoute.amenity,
          index: 0,
          time: selectedRoute.duration,
          onClick: () => handleAmenityClick(selectedRoute.amenity),
          active: true,
          onClose: handleBack,
        }),
        getMainHotspot({
          id,
          address,
          name: city,
          center,
          loading: loadingProp,
          loadingLabel: t("analysis.loading"),
          active: true,
          closable: false,
        }),
      ];
    }

    // When no route, show all filtered amenities
    return [
      // Amenity POI markers (only shown for general type)
      ...(type === "general" && amenities.length > 0 && !loadingProp
        ? amenities
            .filter(
              (
                amenity,
              ): amenity is typeof amenity & { center: [number, number] } =>
                amenity.center !== undefined &&
                (selectedCategories.length === 0 ||
                  (amenity.category !== undefined &&
                    selectedCategories.includes(amenity.category))),
            )
            .map((amenity, index) => {
              const amenityId = `${amenity.name}-${amenity.center[0]}-${amenity.center[1]}`;

              return getAmenityHotspot({
                sessionID: id,
                address,
                amenity,
                index,
                onClick: () => handleAmenityClick(amenity),
                active: selectedAmenityId === amenityId,
              });
            })
        : []),
      // Main house marker
      getMainHotspot({
        id,
        address,
        name: city,
        center,
        loading: loadingProp,
        loadingLabel: t("analysis.loading"),
      }),
    ];
  })();

  // Include route state in resetKey to clear cached hotspots when route becomes active/inactive
  const clusterResetKey = {
    categories: selectedCategories,
    hasRoute: !!selectedRoute,
  };

  /**
   * Apply clustering only for general map type
   */
  const { clusteredHotspots } = useClusteredHotspots({
    sessionID: id,
    hotspots: type === "general" ? hotspots : [],
    currentZoom,
    onClusterClick: handleClusterClick,
    label: t("analysis.mapModal.facilities"),
    resetKey: clusterResetKey,
  });

  /**
   * Use clustered hotspots for general type, regular hotspots for other types
   */
  const displayHotspots = type === "general" ? clusteredHotspots : hotspots;

  const controlsConfig: MapboxMapViewProps["controlsConfig"] = useMemo(
    () => ({
      zoom: true,
      mode: true,
      scrollZoom: true,
    }),
    [],
  );

  /**
   * Generates tooltip content based on the current map type and feature properties
   */
  const getTooltipContent: GetTooltipContentHandler = useCallback(
    (properties) => {
      switch (type) {
        case "neighbourhood":
        case "district":
          // Neighbourhood/District format: r = region name, t = town
          if (properties.r && properties.t) {
            return `<strong>${properties.r}</strong><br/><i>${properties.t}</i>`;
          }
          return null;

        case "foundation":
          // Foundation risk format: fgr = risk category, legenda = legend (format: "Area - XX-XX %"), pc6 = postal code
          if (properties.fgr) {
            const parts = [];

            if (properties.legenda) {
              const legendaStr = String(properties.legenda);
              const legendaParts = legendaStr.split(" - ");

              if (legendaParts.length === 2) {
                const [areaType, percentage] = legendaParts;
                parts.push(`<h3>${percentage}</h3>`);
                parts.push(`${areaType}`);
              } else {
                parts.push(legendaStr);
              }
            }

            parts.push(
              `<strong>${properties.fgr === "Niet indeelbaar" ? t("analysis.mapModal.foundation.variousSoilTypes") : properties.fgr}</strong>`,
            );

            if (properties.pc6) {
              parts.push(
                `<i>${t("analysis.mapModal.foundation.postalCode", { code: properties.pc6 })}</i>`,
              );
            }
            if (!properties.pc6 && properties.pc4) {
              parts.push(
                `<i>${t("analysis.mapModal.foundation.postalCode", { code: properties.pc4 })}</i>`,
              );
            }

            return parts.join("<br/>");
          }
          return null;

        default:
          return null;
      }
    },
    [type, t],
  );

  /**
   * Only apply dynamic opacity for foundation risk layer
   */
  const geoJSONFeatureOpacity = useMemo(
    () => (type === "foundation" ? getFoundationFeatureOpacity : undefined),
    [type],
  );

  return (
    <Modal
      escape={escapable}
      closeable={closeable}
      size={size}
      variant={variant}
      className={containerStyle}
      padding="none"
      onClose={onClose}
      {...props}
    >
      {isLoading && (
        <ProgressBar
          position="absolute"
          align="top"
          variant="infinite"
          className={progressBarStyle}
        />
      )}
      {loadingProp && (
        <MessageBanner
          icon="loading"
          body={t("analysis.mapModal.analyzingMessage")}
          className={messageBannerStyle}
        />
      )}
      {hasGeoJSONError && !loadingProp && (
        <MessageBanner
          icon="alertFilled"
          body={t("analysis.mapModal.mapError")}
          variant="error"
          className={messageBannerStyle}
        />
      )}
      <Select
        className={cardStyle}
        options={mapTypeOptions}
        value={type}
        onChange={(value) => setType(value as MapModalType)}
        disabled={loadingProp}
        leading={<Icon icon="map" size="s2" />}
        label={t("analysis.mapModal.mapLabel")}
      />

      <MapboxMapView
        ref={mapRef}
        accessToken={mapBoxToken}
        center={center}
        hotspots={displayHotspots}
        geoJSON={geoJSON}
        getTooltipContent={getTooltipContent}
        getGeoJSONFeatureOpacity={geoJSONFeatureOpacity}
        controlsConfig={controlsConfig}
        onReady={handleReady}
        onViewportChange={handleViewportChange}
        customControlsTrailing={
          <Tooltip value={t("analysis.mapModal.close")}>
            <IconButton
              icon="close"
              onClick={onClose}
              size="xs"
              variant="background"
              border="default"
              className={mergeClasses(closeButtonStyle, elevations.m)}
              rounded
            />
          </Tooltip>
        }
      />

      {type === "general" && !loadingProp && (
        <MapControlCard
          ref={mapControlCardRef}
          categories={categories}
          selectedCategories={selectedCategories}
          onCategoryToggle={handleCategoryToggle}
          getCategoryCount={getCategoryCount}
          selectedRoute={selectedRoute}
          onBack={handleBack}
        />
      )}
    </Modal>
  );
};
